import React, { useState, useEffect, useCallback } from 'react'
import {
  Card, Form, Button, Row, Col, Select, DatePicker,
  InputNumber, Divider, message, Radio, Steps, Descriptions,
  Alert, Statistic, Tooltip, Badge,
} from 'antd'
import {
  ArrowLeftOutlined, SaveOutlined, SendOutlined,
  ThunderboltOutlined, UserOutlined, CalendarOutlined,
  CheckCircleOutlined, InfoCircleOutlined, ReloadOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import { useMasterData } from '@/context/MasterDataContext'
import { meterReadingStore, MeterReadingRow } from './meterReadingStore'
import { api } from '@/services/api'
import { authStore } from '@/context/auth'
import './MeterReading.css'

const SLOT_COLORS: Record<string, string> = { 'off-peak': '#8B5CF6', 'normal': '#2563EB', 'peak': '#DC2626' }
const SLOT_BGS:   Record<string, string> = { 'off-peak': '#F5F3FF', 'normal': '#EFF4FF', 'peak': '#FEF2F2' }
const SLOT_CAT:   Record<string, string> = { 'off-peak': 'Off-Peak', 'normal': 'Normal', 'peak': 'Peak' }

/* ── Approval Workflow steps ────────────────────────────── */
const APPROVAL_STEPS = [
  { title: 'Entry',    description: 'Operator enters reading' },
  { title: 'Submit',   description: 'Send for supervisor review' },
  { title: 'Review',   description: 'Supervisor validates values' },
  { title: 'Approved', description: 'Reading feeds allocation' },
]

/* ── Form Page ──────────────────────────────────────────── */
const MeterReadingForm: React.FC = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = !!id
  const isAdmin = authStore.isAdmin()

  const { generators: masterGens, consumers: masterCons, slots: masterSlots } = useMasterData()

  // Slot codes are named C1, C2, C3 … — sort by the numeric suffix so the
  // entry table and every slot-wise readout always follow slot order,
  // regardless of how the Slot Master list happens to be ordered.
  const slotSortKey = (code: string): number => {
    const n = parseInt((code || '').replace(/\D/g, ''), 10)
    return isNaN(n) ? Number.MAX_SAFE_INTEGER : n
  }

  const SLOT_MASTER = masterSlots
    .filter(s => s.status === 'active')
    .slice()
    .sort((a, b) => slotSortKey(a.slotCode) - slotSortKey(b.slotCode))
    .map(s => ({
      id:      s.slotCode,
      label:   `${s.slotCode} — ${SLOT_CAT[s.category]}`,
      from:    s.startTime,
      to:      s.endTime,
      color:   SLOT_COLORS[s.category] ?? '#4A5568',
      bgColor: SLOT_BGS[s.category]   ?? '#F1F4F9',
    }))

  const GENERATORS = masterGens
    .filter(g => g.status === 'active')
    .map(g => ({ value: g.id, label: `${g.name} (${g.code})` }))

  const CONSUMERS = masterCons
    .filter(c => c.status === 'active')
    .map(c => ({ value: c.id, label: `${c.name} (${c.code})` }))

  const [readingType, setReadingType] = useState<'generator' | 'consumer'>('generator')
  const [dayCount, setDayCount]       = useState<number>(0)
  const [slotUnits, setSlotUnits]     = useState<Record<string, number>>(
    () => Object.fromEntries(masterSlots.filter(s => s.status === 'active').map(s => [s.slotCode, 0]))
  )
  const [totalUnits, setTotalUnits]   = useState<number>(0)
  const [currentStep, setCurrentStep] = useState<number>(isEditing ? 2 : 0)
  const [saving, setSaving]           = useState(false)

  /* Plant selection — a generator can operate multiple plants, so a reading
     must say which one it's for. Not applicable to consumer readings. */
  const entityId = Form.useWatch('entityId', form) as string | undefined
  const plantId  = Form.useWatch('plantId', form) as string | undefined
  const selectedGenerator = readingType === 'generator' ? masterGens.find(g => g.id === entityId) : undefined
  const selectedConsumer  = readingType === 'consumer' ? masterCons.find(c => c.id === entityId) : undefined
  const plantList = readingType === 'generator'
    ? (selectedGenerator?.plants ?? []).filter(p => p.status !== 'inactive')
    : (selectedConsumer?.services ?? []).filter(s => s.status !== 'inactive')
  const plantOptions = plantList.map(p => {
    if (readingType === 'generator') {
      const gp = p as any
      return {
        value: gp.id,
        label: `${gp.plantName || gp.id} — ${gp.htServiceNumber || 'no HT'} · ${Number(gp.capacity || 0).toLocaleString('en-IN')} kWh/yr`,
      }
    } else {
      const cs = p as any
      return {
        value: cs.id,
        label: `${cs.plantName || cs.id} — ${cs.htscNumber || 'no HT'} · ${Number(cs.sanctionedDemand || 0).toLocaleString('en-IN')} kVA`,
      }
    }
  })
  const selectedPlant = plantList.find(p => p.id === plantId)

  /* Recalculate total whenever slot values change */
  useEffect(() => {
    setTotalUnits(Object.values(slotUnits).reduce((a, b) => Number(a) + Number(b), 0))
  }, [slotUnits])

  /* Hydrate slotUnits whenever active masterSlots load */
  useEffect(() => {
    if (masterSlots.length > 0) {
      setSlotUnits(prev => {
        const activeSlots = masterSlots.filter(s => s.status === 'active')
        const updated = { ...prev }
        let changed = false
        activeSlots.forEach(s => {
          if (updated[s.slotCode] === undefined) {
            updated[s.slotCode] = 0
            changed = true
          }
        })
        return changed ? updated : prev
      })
    }
  }, [masterSlots])

  /* Recalculate day count on date changes */
  const handleDateChange = useCallback(() => {
    const start: Dayjs | null = form.getFieldValue('startDate')
    const end:   Dayjs | null = form.getFieldValue('endDate')
    if (start && end && end.isAfter(start)) {
      setDayCount(end.diff(start, 'day') + 1)
    } else {
      setDayCount(0)
    }
  }, [form])

  // Load store on mount so in-memory cache is hydrated before duplicate checks
  useEffect(() => {
    meterReadingStore.refresh()
  }, [])

  const startDateVal = Form.useWatch('startDate', form)
  const endDateVal   = Form.useWatch('endDate', form)
  const startStr     = startDateVal && dayjs.isDayjs(startDateVal) ? startDateVal.format('YYYY-MM-DD') : (typeof startDateVal === 'string' ? startDateVal : '')
  const endStr       = endDateVal && dayjs.isDayjs(endDateVal) ? endDateVal.format('YYYY-MM-DD') : (typeof endDateVal === 'string' ? endDateVal : '')

  /* Pre-fill if editing — load the saved record from the store */
  useEffect(() => {
    if (isEditing && id) {
      const existing = meterReadingStore.get(id)
      if (existing) {
        const start = dayjs(existing.startDate)
        const end = dayjs(existing.endDate)
        form.setFieldsValue({
          type:      existing.type,
          entityId:  existing.entityId,
          plantId:   existing.plantId,
          startDate: start,
          endDate:   end,
        })
        setReadingType(existing.type)
        setSlotUnits(
          Object.fromEntries(existing.slots.map(s => [s.slotName, s.units]))
        )
        setDayCount(start.isValid() && end.isValid() ? end.diff(start, 'day') + 1 : 0)
        setCurrentStep(2)
      }
    } else if (!isEditing) {
      const start = dayjs().startOf('month')
      const end = dayjs().endOf('month')
      form.setFieldsValue({
        startDate: start,
        endDate:   end,
      })
      setDayCount(end.diff(start, 'day') + 1)
    }
  }, [isEditing, id, form])

  const handleSlotChange = (slotId: string, value: number | null) => {
    setSlotUnits(prev => ({ ...prev, [slotId]: value ?? 0 }))
  }

  const handleTypeChange = (val: 'generator' | 'consumer') => {
    setReadingType(val)
    form.setFieldsValue({ entityId: undefined, plantId: undefined })
  }

  // Reset the plant choice when the generator changes; auto-pick if there's only one.
  const handleEntityChange = (val: string) => {
    const activePlants = readingType === 'generator'
      ? (masterGens.find(g => g.id === val)?.plants ?? []).filter(p => p.status !== 'inactive')
      : (masterCons.find(c => c.id === val)?.services ?? []).filter(s => s.status !== 'inactive')
    form.setFieldValue('plantId', activePlants.length === 1 ? activePlants[0].id : undefined)
  }

  /* Build a persistable MeterReadingRow from current form + slot state */
  const buildRecord = (status: string): Omit<MeterReadingRow, 'id'> => {
    const entityId: string = form.getFieldValue('entityId') || ''
    const start: Dayjs | null = form.getFieldValue('startDate')
    const end:   Dayjs | null = form.getFieldValue('endDate')
    const entity = readingType === 'generator'
      ? masterGens.find(g => g.id === entityId)
      : masterCons.find(c => c.id === entityId)
    const plantId = form.getFieldValue('plantId') || undefined
    const plant = plantId
      ? (readingType === 'generator'
          ? (entity as any)?.plants?.find((p: any) => p.id === plantId)
          : (entity as any)?.services?.find((s: any) => s.id === plantId))
      : undefined
    const slots = Object.entries(slotUnits).map(([slotName, units]) => ({ slotName, units }))
    const total = Object.values(slotUnits).reduce((a, b) => Number(a) + Number(b), 0)
    const existingRecord = isEditing && id ? meterReadingStore.get(id) : undefined
    const nowIso = new Date().toISOString()
    return {
      type:        readingType,
      entityId,
      entityName:  entity?.name,
      plantId,
      plantName:   plant?.plantName,
      startDate:   start ? start.format('YYYY-MM-DD') : '',
      endDate:     end ? end.format('YYYY-MM-DD') : '',
      slots,
      totalUnits:  total,
      status,
      submittedBy: 'Admin User',
      submittedAt: dayjs().format('YYYY-MM-DD'),
      createdAt:   existingRecord?.createdAt || nowIso,
      updatedAt:   nowIso,
    }
  }

  // One reading per plant/service per month / period — a second reading
  // covering a month that's already been read for that specific plant is a duplicate.
  // Rule: oru plant/service ku oru month ku ONE reading.
  const findDuplicateReading = (
    type: 'generator' | 'consumer', entityId: string, plantId: string | undefined,
    start: string, end: string, excludeId?: string,
  ): MeterReadingRow | undefined => {
    if (!entityId || !start || !end) return undefined

    // If the entity has plants/services configured, wait until a plant is chosen
    // because different plants have independent meter readings.
    const entity = type === 'generator'
      ? masterGens.find(g => g.id === entityId)
      : masterCons.find(c => c.id === entityId)
    const availablePlants = type === 'generator'
      ? (entity as any)?.plants
      : (entity as any)?.services
    if (availablePlants && availablePlants.length > 0 && !plantId) {
      return undefined
    }

    const startMonth = start.slice(0, 7)
    const endMonth   = end.slice(0, 7)
    return meterReadingStore.getAll().find(r => {
      if (r.id === excludeId) return false
      if (r.type !== type || r.entityId !== entityId) return false
      if (r.status === 'deleted') return false

      // Plant/service isolation:
      // If plantId is selected and existing record has r.plantId,
      // they are duplicates ONLY IF r.plantId === plantId.
      if (plantId && r.plantId && r.plantId !== plantId) return false

      const rStartMonth = (r.startDate || '').slice(0, 7)
      const rEndMonth   = (r.endDate || '').slice(0, 7)

      const monthMatch =
        (!!startMonth && rStartMonth === startMonth) ||
        (!!endMonth && rStartMonth === endMonth) ||
        (!!startMonth && rEndMonth === startMonth)

      const rangeOverlap = !(
        dayjs(end).isBefore(dayjs(r.startDate), 'day') ||
        dayjs(start).isAfter(dayjs(r.endDate), 'day')
      )

      return monthMatch || rangeOverlap
    })
  }

  const duplicateReading = findDuplicateReading(
    readingType,
    entityId || '',
    plantId,
    startStr,
    endStr,
    isEditing ? id : undefined
  )

  // Code Master's counter for this module only remembers ONE "current"
  // ── Meter Reading ID generator ────────────────────────────────────────────
  //
  // The backend Code Master stores ONE prefix + ONE running number per module
  // key. If we call generateCode('meter_readings', 'RID2026-JUL') it resets
  // the sequence to 1 — and then the next AUG call resets it back, creating
  // duplicate IDs (RID2026-AUG001 reused, etc.).
  //
  // Instead we use the Code Master only as a FORMAT TEMPLATE:
  //   prefix in DB: "RID2026-AUG"  → strip month → base: "RID2026-"
  //   For a Jul reading: periodPrefix = "RID2026-JUL"
  //   Scan existing records to find highest existing "RID2026-JUL###" number
  //   Return next free "RID2026-JUL###"
  //
  // Each month gets its own independent sequence derived purely from what is
  // already stored in the database — no backend counter involved.
  const makeReadingId = async (startDate: string): Promise<string> => {
    // Refresh store to make sure we have the latest IDs from the DB
    const allRecords = meterReadingStore.getAll()
    const existingIds = new Set(allRecords.map(r => r.id))

    // Derive 3-letter month from reading period start date
    // Use local date arithmetic: '2026-07-01' → July → 'JUL'
    const dateParts = startDate.split('-')
    const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC']
    const periodMonth = dateParts.length >= 2 ? MONTHS[parseInt(dateParts[1], 10) - 1] : null

    // Step 1: Fetch Code Master row to get the configured prefix (format template)
    let periodPrefix: string | null = null
    let padding = 3
    try {
      const rows = await api.get<any[]>('/code-masters')
      const cmRow = rows.find((r: any) =>
        (r.moduleKey || '').toLowerCase().trim() === 'meter_readings' &&
        r.status !== 'deleted'
      )
      if (cmRow?.prefix && periodMonth) {
        const configuredPrefix: string = cmRow.prefix
        padding = cmRow.padding || 3
        // Replace the month abbreviation in the stored prefix with the period month
        // e.g. "RID2026-AUG" → "RID2026-JUL" for a July reading
        const MONTH_REGEX = /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/i
        if (MONTH_REGEX.test(configuredPrefix)) {
          periodPrefix = configuredPrefix.replace(MONTH_REGEX, periodMonth)
        } else {
          // No month in prefix — just append month to distinguish per-month sequences
          periodPrefix = `${configuredPrefix}-${periodMonth}`
        }
      }
    } catch { /* API unreachable — fall through to local fallback */ }

    // Step 2: Build period-specific prefix from period month if Code Master unreachable
    if (!periodPrefix && periodMonth) {
      periodPrefix = `RID2026-${periodMonth}`
    }
    if (!periodPrefix) periodPrefix = 'MR'

    // Step 3: Scan all existing readings with this period prefix and find the max number
    let max = 0
    for (const rid of existingIds) {
      if (!rid.startsWith(periodPrefix)) continue
      const numStr = rid.slice(periodPrefix.length)
      const num = parseInt(numStr, 10)
      if (!isNaN(num) && num > max) max = num
    }

    // Step 4: Return next free ID (handle unlikely collision from concurrent saves)
    let next = max + 1
    let candidate = `${periodPrefix}${String(next).padStart(padding, '0')}`
    while (existingIds.has(candidate)) {
      next += 1
      candidate = `${periodPrefix}${String(next).padStart(padding, '0')}`
    }
    return candidate
  }

  const existingRecord = isEditing && id ? meterReadingStore.get(id) : undefined
  const isApproved = existingRecord?.status === 'Approved'

  const onSaveDraft = async () => {
    if (isEditing && isApproved && !isAdmin) {
      message.error('This reading is Approved. Only administrators can edit approved meter readings.')
      return
    }
    const record = buildRecord('Draft')
    // MeterReadingForm never otherwise refreshes meterReadingStore itself —
    // on a fresh page load straight at /meter-readings/new (no prior visit to
    // the list page this session), the cache the duplicate check and id
    // generator both read from could still be empty/stale, silently
    // defeating both. Always get current data right before relying on it.
    await meterReadingStore.refresh()
    const dupe = findDuplicateReading(record.type, record.entityId, record.plantId, record.startDate, record.endDate, isEditing ? id : undefined)
    if (dupe) {
      message.error(`A reading already exists for this ${record.type} (and period) — ${dupe.id} (${dupe.status}, ${dupe.startDate} → ${dupe.endDate}). Edit that one instead of creating a duplicate.`)
      return
    }
    setSaving(true)
    try {
      await new Promise(r => setTimeout(r, 600))
      if (isEditing && id) {
        meterReadingStore.save({ id, ...record })
      } else {
        // create(), not save() — a generated id that happens to collide with
        // an existing record must never be silently treated as an edit of it.
        meterReadingStore.create({ id: await makeReadingId(record.startDate), ...record })
      }
      message.success({ content: 'Draft saved successfully!', icon: <SaveOutlined style={{ color: '#001A4D' }} /> })
    } finally {
      setSaving(false)
    }
  }

  const onSubmit = async () => {
    if (isEditing && isApproved && !isAdmin) {
      message.error('This reading is Approved. Only administrators can edit approved meter readings.')
      return
    }
    try {
      await form.validateFields()
    } catch {
      message.error('Please fill all required fields before submitting.')
      return
    }
    try {
      const record = buildRecord('Submitted')
      await meterReadingStore.refresh()
      const dupe = findDuplicateReading(record.type, record.entityId, record.plantId, record.startDate, record.endDate, isEditing ? id : undefined)
      if (dupe) {
        message.error(`A reading already exists for this ${record.type} (and period) — ${dupe.id} (${dupe.status}, ${dupe.startDate} → ${dupe.endDate}). Edit that one instead of creating a duplicate.`)
        return
      }
      setSaving(true)
      await new Promise(r => setTimeout(r, 800))
      if (isEditing && id) {
        meterReadingStore.save({ id, ...record })
      } else {
        meterReadingStore.create({ id: await makeReadingId(record.startDate), ...record })
      }
      message.success('Reading submitted for supervisor approval!')
      navigate('/meter-readings')
    } catch {
      message.error('Failed to submit the reading. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const entityOptions = readingType === 'generator' ? GENERATORS : CONSUMERS
  const slotSum = Object.values(slotUnits).reduce((a, b) => Number(a) + Number(b), 0)

  return (
    <div className="mrf-container">

      {/* ── Page Header ──────────────────────────────────── */}
      <div className="mrf-header">
        <div className="mrf-header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/meter-readings')}
            style={{ flexShrink: 0, marginTop: 4 }}
          />
          <div>
            <h2>{isEditing ? `Edit Reading — ${id}` : 'New Meter Reading'}</h2>
            <p className="mrf-header-sub">
              {isEditing
                ? 'Correct and resubmit for supervisor approval'
                : 'Capture slot-wise generation or consumption units for a billing period'}
            </p>
          </div>
        </div>
      </div>

      {/* ── Approval Workflow Banner ──────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <Steps
          current={currentStep}
          size="small"
          items={APPROVAL_STEPS.map((s, i) => ({
            title: s.title,
            description: s.description,
            icon: i < currentStep
              ? <CheckCircleOutlined style={{ color: '#00A651' }} />
              : i === currentStep
              ? <ClockCircleOutlined style={{ color: '#001A4D' }} />
              : undefined,
          }))}
          style={{
            background: '#fff', border: '1px solid #E4E8F0',
            borderRadius: 12, padding: '14px 24px',
          }}
        />
      </div>

      {/* ── Approved Edit Restriction Banners ──────────────── */}
      {isEditing && isApproved && !isAdmin && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 20, borderRadius: 12, border: '1.5px solid #FCD34D' }}
          message={<strong style={{ fontSize: 14 }}>Approved Meter Reading — Edit Restricted</strong>}
          description="This meter reading is Approved. Only administrators can edit approved meter readings. Contact your administrator if changes are required."
        />
      )}

      {isEditing && isApproved && isAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 20, borderRadius: 12, border: '1.5px solid #93C5FD' }}
          message={<strong style={{ fontSize: 14 }}>Administrator Edit Mode</strong>}
          description="This meter reading is currently Approved. As an Administrator, you are editing an approved reading."
        />
      )}

      {/* ── Duplicate Alert Banner ────────────────────────── */}
      {duplicateReading && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 20, borderRadius: 12, border: '1.5px solid #FCA5A5' }}
          message={
            <strong style={{ fontSize: 14 }}>
              Duplicate Meter Reading Not Allowed
            </strong>
          }
          description={
            <div>
              A meter reading already exists for this {readingType} ({selectedGenerator?.name || selectedConsumer?.name || duplicateReading.entityName || duplicateReading.entityId}{duplicateReading.plantName ? ` — ${duplicateReading.plantName}` : ''}) for{' '}
              <strong>
                {dayjs(duplicateReading.startDate).isValid()
                  ? dayjs(duplicateReading.startDate).format('MMM YYYY')
                  : duplicateReading.startDate}
              </strong>{' '}
              (Reading ID: <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{duplicateReading.id}</strong> — Status: <strong>{duplicateReading.status}</strong>, Period: {duplicateReading.startDate} → {duplicateReading.endDate}).
              <br />
              <div style={{ marginTop: 6 }}>
                <strong>Rule:</strong> Each plant or consumer service can only have <u>one meter reading per month</u>. Please edit the existing record from the list instead of creating a duplicate for the same plant.
              </div>
            </div>
          }
        />
      )}

      <Form form={form} layout="vertical" autoComplete="off">

        {/* ── SECTION 1: Reading Type & Entity ─────────────── */}
        <Card className="mrf-section-card">
          <div className="mrf-section-title">
            <div className="mrf-section-num">1</div>
            Reading Type & Entity
          </div>

          <Row gutter={[20, 0]}>
            <Col xs={24} md={12}>
              <Form.Item label={<strong>Reading Type</strong>} name="type" initialValue="generator" required>
                <Radio.Group
                  className="mrf-type-group"
                  onChange={e => handleTypeChange(e.target.value)}
                  value={readingType}
                >
                  <Radio.Button value="generator">
                    <ThunderboltOutlined style={{ marginRight: 6 }} />Generator
                  </Radio.Button>
                  <Radio.Button value="consumer">
                    <UserOutlined style={{ marginRight: 6 }} />Consumer
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="entityId"
                label={<strong>{readingType === 'generator' ? 'Select Generator' : 'Select Consumer'}</strong>}
                rules={[{ required: true, message: `Please select a ${readingType}` }]}
              >
                <Select
                  showSearch
                  placeholder={`Choose ${readingType}...`}
                  size="large"
                  options={entityOptions}
                  style={{ width: '100%' }}
                  onChange={handleEntityChange}
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            {entityId && (
              <Col xs={24} md={12}>
                <Form.Item
                  name="plantId"
                  label={<strong>{readingType === 'generator' ? 'Select Plant' : 'Select Service / Plant'}</strong>}
                  rules={plantOptions.length ? [{ required: true, message: `Please select a ${readingType === 'generator' ? 'plant' : 'service/plant'}` }] : []}
                  extra={!plantOptions.length ? `This ${readingType} has no active plants/services — please add them first.` : undefined}
                >
                  <Select
                    showSearch
                    placeholder={plantOptions.length ? `Choose ${readingType === 'generator' ? 'plant' : 'service/plant'}...` : `No active ${readingType === 'generator' ? 'plants' : 'services'} available`}
                    size="large"
                    options={plantOptions}
                    disabled={!plantOptions.length}
                    style={{ width: '100%' }}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Entity preview banner */}
          <div style={{
            background: readingType === 'generator' ? '#FFFBEB' : '#EFF4FF',
            border: `1px solid ${readingType === 'generator' ? '#FDE68A' : '#BFDBFE'}`,
            borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#4A5568',
          }}>
            <strong>{readingType === 'generator' ? '⚡ Generator Reading' : '👤 Consumer Reading'}:</strong>&nbsp;
            {readingType === 'generator'
              ? 'Units produced by the generator across all time slots for the selected period.'
              : 'Units consumed by the consumer across all time slots for the selected period.'}
          </div>
        </Card>

        {/* ── SECTION 2: Reading Period ─────────────────────── */}
        <Card className="mrf-section-card">
          <div className="mrf-section-title">
            <div className="mrf-section-num">2</div>
            <CalendarOutlined style={{ marginRight: 4, fontSize: 15 }} />
            Reading Period
          </div>

          <Row gutter={[20, 0]} align="middle">
            <Col xs={24} sm={8}>
              <Form.Item
                name="startDate"
                label={<strong>Start Date</strong>}
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker
                  size="large"
                  format="DDMMMYYYY"
                  style={{ width: '100%' }}
                  placeholder="e.g. 08Jun2026"
                  onChange={handleDateChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="endDate"
                label={<strong>End Date</strong>}
                rules={[
                  { required: true, message: 'Required' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || !getFieldValue('startDate') || value.isAfter(getFieldValue('startDate'))) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('End date must be after start date'))
                    },
                  }),
                ]}
              >
                <DatePicker
                  size="large"
                  format="DDMMMYYYY"
                  style={{ width: '100%' }}
                  placeholder="e.g. 08Jun2026"
                  onChange={handleDateChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label={<strong>Duration (Auto)</strong>}>
                <div className="mrf-day-badge">
                  <CalendarOutlined style={{ color: '#2563EB' }} />
                  {dayCount > 0 ? `${dayCount} Days` : '— Days'}
                </div>
                <div style={{ fontSize: 11.5, color: '#8A93A6', marginTop: 4 }}>
                  Calculated automatically
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── SECTION 3: Slot-wise Units ────────────────────── */}
        <Card className="mrf-section-card">
          <div className="mrf-section-title">
            <div className="mrf-section-num">3</div>
            Slot-wise Unit Entry
            <Tooltip title="Slot definitions (name, from/to time) are pulled from the Slot Master (Module 14).">
              <InfoCircleOutlined style={{ color: '#8A93A6', fontSize: 14, cursor: 'help' }} />
            </Tooltip>
          </div>

          {/* Slot rows */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {SLOT_MASTER.map(slot => {
              const val   = slotUnits[slot.id] || 0
              const pct   = slotSum > 0 ? ((val / slotSum) * 100).toFixed(1) : '0'
              return (
                <div key={slot.id} style={{
                  border: `1.5px solid ${slot.color}30`,
                  borderRadius: 10,
                  overflow: 'hidden',
                }}>
                  <Row align="middle" gutter={0} style={{ background: slot.bgColor }}>
                    {/* Slot name & time */}
                    <Col xs={24} sm={8} style={{ padding: '14px 16px', borderRight: `1px solid ${slot.color}20` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: slot.color, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 800, color: slot.color, fontSize: 14 }}>{slot.id}</div>
                          <div style={{ fontSize: 13, color: '#4A5568', fontWeight: 600 }}>
                            {slot.label.split('—')[1]?.trim()}
                          </div>
                          <div style={{ fontSize: 11.5, color: '#8A93A6' }}>
                            {slot.from} → {slot.to}
                          </div>
                        </div>
                      </div>
                    </Col>

                    {/* Units input */}
                    <Col xs={24} sm={8} style={{ padding: '14px 16px', borderRight: `1px solid ${slot.color}20` }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: '#8A93A6', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                        Units (kWh)
                      </div>
                      <InputNumber
                        min={0}
                        max={9999999}
                        step={100}
                        value={slotUnits[slot.id]}
                        onChange={v => handleSlotChange(slot.id, v)}
                        size="large"
                        style={{ width: '100%' }}
                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={v => Number(v?.replace(/,/g, '') || 0)}
                        addonAfter="kWh"
                      />
                    </Col>

                    {/* Percentage bar */}
                    <Col xs={24} sm={8} style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 11.5, color: '#8A93A6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                          Share
                        </span>
                        <span style={{ fontWeight: 800, color: slot.color, fontSize: 14 }}>
                          {pct}%
                        </span>
                      </div>
                      <div style={{ height: 8, background: '#E4E8F0', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', background: slot.color,
                          borderRadius: 4, width: `${pct}%`,
                          transition: 'width .3s ease',
                        }} />
                      </div>
                      <div style={{ fontSize: 12, color: '#4A5568', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>
                        {val.toLocaleString('en-IN')} kWh
                      </div>
                    </Col>
                  </Row>
                </div>
              )
            })}
          </div>

          {/* Total Units Box */}
          <div className="mrf-total-box">
            <div>
              <div className="mrf-total-label">Total Units (Auto-calculated)</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
                C1 + C2 + C3 combined
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <div className="mrf-total-value">
                {totalUnits.toLocaleString('en-IN')}
              </div>
              <span className="mrf-total-unit">kWh</span>
            </div>
          </div>

          {totalUnits > 0 && (
            <div style={{ marginTop: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#166534' }}>
              <strong>≈ {(totalUnits / 1000).toFixed(2)} MWh</strong> for the selected period
              {dayCount > 0 && (
                <span style={{ marginLeft: 10, color: '#4A7856' }}>
                  · Avg <strong>{(totalUnits / dayCount).toFixed(0)} kWh/day</strong>
                </span>
              )}
            </div>
          )}
        </Card>

        {/* ── SECTION 4: Approval Workflow Info ────────────── */}
        <Card className="mrf-section-card">
          <div className="mrf-section-title">
            <div className="mrf-section-num">4</div>
            Approval Workflow
          </div>

          <div className="mrf-approval-info">
            <div className="mrf-approval-icon">⚠️</div>
            <div>
              <div className="mrf-approval-label">Supervisor Approval Required</div>
              <div className="mrf-approval-sub">
                Once submitted, the reading will enter the review queue. A supervisor must approve before values
                feed into unit allocation. If rejected, you will be notified with a correction reason.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Row gutter={[12, 12]}>
              {[
                { step: '1', title: 'Operator Submits', desc: 'You fill and submit this form', color: '#2563EB' },
                { step: '2', title: 'Supervisor Reviews', desc: 'Validates against TANGEDCO records', color: '#F59E0B' },
                { step: '3', title: 'Approved / Rejected', desc: 'Notified with reason if rejected', color: '#DC2626' },
                { step: '4', title: 'Allocation Feed', desc: 'Approved reading used in allocation', color: '#00A651' },
              ].map(item => (
                <Col xs={24} sm={12} md={6} key={item.step}>
                  <div style={{
                    background: '#F8FAFC', border: '1px solid #E4E8F0',
                    borderRadius: 8, padding: '12px 14px',
                    borderLeft: `3px solid ${item.color}`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: item.color, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>
                      Step {item.step}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0B1426' }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: '#8A93A6', marginTop: 2 }}>{item.desc}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Card>

        {/* ── Summary ──────────────────────────────────────── */}
        {slotSum > 0 && (
          <Card
            className="mrf-section-card"
            style={{ background: 'linear-gradient(135deg, #001A4D 0%, #1E3A8A 100%)', border: 'none' } as any}
          >
            <div style={{ color: '#fff' }}>
              <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-.01em', marginBottom: 16, opacity: .9 }}>
                📊 Reading Summary
              </div>
              <Row gutter={[20, 16]}>
                {[
                  { label: 'Reading Type', value: readingType.charAt(0).toUpperCase() + readingType.slice(1) },
                  { label: 'Period',       value: dayCount > 0 ? `${dayCount} days` : '—' },
                  { label: 'Total Units',  value: `${totalUnits.toLocaleString('en-IN')} kWh` },
                  { label: 'MWh Equiv',    value: `${(totalUnits / 1000).toFixed(2)} MWh` },
                ].map(item => (
                  <Col xs={12} sm={6} key={item.label}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', marginTop: 4, fontFamily: 'JetBrains Mono, monospace' }}>
                      {item.value}
                    </div>
                  </Col>
                ))}
              </Row>
              <Divider style={{ borderColor: 'rgba(255,255,255,.15)', margin: '16px 0' }} />
              <Row gutter={[12, 8]}>
                {SLOT_MASTER.map(slot => {
                  const val = slotUnits[slot.id] || 0
                  const pct = slotSum > 0 ? ((val / slotSum) * 100).toFixed(1) : '0'
                  return (
                    <Col xs={24} sm={8} key={slot.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', fontWeight: 700 }}>{slot.id}</span>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,.9)', fontWeight: 800 }}>{pct}%</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,.15)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: slot.color, borderRadius: 3, width: `${pct}%`, transition: 'width .3s ease' }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.6)', marginTop: 3 }}>
                        {val.toLocaleString('en-IN')} kWh
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </div>
          </Card>
        )}

        {/* ── Form Actions ─────────────────────────────────── */}
        <div className="mrf-actions">
          <Button
            icon={<ArrowLeftOutlined />}
            size="large"
            onClick={() => navigate('/meter-readings')}
          >
            Back to List
          </Button>
          <div className="mrf-actions-right">
            <Button
              icon={<SaveOutlined />}
              size="large"
              loading={saving}
              disabled={!!duplicateReading || (isEditing && isApproved && !isAdmin)}
              onClick={onSaveDraft}
              style={{ fontWeight: 700 }}
            >
              Save Draft
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              size="large"
              loading={saving}
              disabled={!!duplicateReading || (isEditing && isApproved && !isAdmin)}
              onClick={onSubmit}
              style={{ fontWeight: 700, background: (duplicateReading || (isEditing && isApproved && !isAdmin)) ? undefined : '#001A4D', borderColor: (duplicateReading || (isEditing && isApproved && !isAdmin)) ? undefined : '#001A4D' }}
            >
              {isEditing ? 'Resubmit for Approval' : 'Submit for Approval'}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  )
}

export default MeterReadingForm
